import React from 'react';

import { MOBILE_NAV_ANIM } from '../../../../theme/tokens';

/**
 * Reports whether the mobile bottom bar should hide itself: `true` while the user
 * scrolls DOWN the page (content moving up), `false` while scrolling UP, near the
 * top, or near the bottom. Reads the WINDOW scroll — on mobile the nova
 * layout makes the document the scroller (see `MainLayout`'s `MobileBodyScroll`),
 * so `window.scrollY` tracks the page.
 *
 * It also reveals near the BOTTOM of the page: the `Page` reserves a fixed
 * bottom-nav clearance, so once you reach the end of a downward scroll the bar
 * slides back into that slot instead of leaving an empty gap — and because the
 * clearance is constant the content never reflows when the bar hides/shows.
 *
 * The hide/show thresholds are ASYMMETRIC, measured as net travel from the last
 * toggle point (`anchorY`): a short downward travel hides the bar, but it only
 * comes back after a deliberate, larger UPWARD travel — so a small up nudge (or
 * momentum jitter) never pops it in. Re-anchoring on each toggle keeps the dead
 * band centred on where you last reversed.
 *
 * `enabled` gates the whole thing (false → never hide, listener detached): the
 * caller passes `false` on desktop (no bottom bar) and under reduced-motion (the
 * bar simply stays put). rAF-throttled.
 */
export const useHideOnScroll = (enabled: boolean): boolean => {
  const [hidden, setHidden] = React.useState(false);

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setHidden(false);
      return undefined;
    }

    let anchorY = window.scrollY; // the scroll position of the last hide/show toggle
    let frame = 0;

    const update = (): void => {
      frame = 0;
      const y = window.scrollY;
      const nearTop = y <= MOBILE_NAV_ANIM.scrollTopRevealPx;
      const nearBottom =
        window.innerHeight + y >= document.documentElement.scrollHeight - MOBILE_NAV_ANIM.scrollBottomRevealPx;
      // Always reveal (and re-anchor) at the page ends.
      if (nearTop || nearBottom) {
        anchorY = y;
        setHidden(false);
        return;
      }
      // travel: + = net DOWN from the anchor, - = net UP. Hide on a short down
      // travel; only reveal after a larger up travel; re-anchor on each toggle.
      const travel = y - anchorY;
      if (travel >= MOBILE_NAV_ANIM.scrollDownHidePx) {
        setHidden(true);
        anchorY = y;
      } else if (travel <= -MOBILE_NAV_ANIM.scrollUpRevealPx) {
        setHidden(false);
        anchorY = y;
      }
    };

    const onScroll = (): void => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return hidden;
};

export default useHideOnScroll;
